/**
 * @fileoverview Hook Data Layer para mensajes de pedido WhatsApp (OrderWsMessageMC).
 * @module useOrderWsMessageData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../../../util/numberUtils';
import { isWhatsAppMsPlatform } from '../../../../services/inbox/inboxMsChatPlatform';

/**
 * Hook Data Layer para mensajes de pedido WhatsApp (OrderWsMessageMC).
 *
 * Implementa el patrón de **Data Layer**: solo gestiona la carga de datos, estados de loading/error y serialización de texto/imagen.
 * No contiene lógica de UI ni de negocio.
 *
 * @function useOrderWsMessageData
 * @category Hook - Data Layer
 *
 * @param {Object} params - Parámetros del hook
 * @param {string} params.platform - Plataforma del mensaje ("whatsapp", etc.)
 * @param {Object} params.message - Objeto mensaje completo
 *
 * @returns {Object} Retorna objeto memoizado con:
 *   - data: Objeto con contentMediaUrl y resto de message
 *   - messageText: Texto serializado para mostrar resumen de productos y total
 *
 * @example
 * const { data, messageText } = useOrderWsMessageData({ platform, message });
 *
 * @see OrderWsMessageMC
 * @since 1.0.0
 * @version 1.0.0
 */
export default function useOrderWsMessageData({ platform, message }) {
  const { t } = useTranslation('InboxMC');
  const [url, setUrl] = useState('');
  const [messageText, setMessageText] = useState('');

  useEffect(() => {
    // El inbox MS manda el enum canónico (whatsapp_bsp_messages / _qr_ /
    // _gupshup_), no la clave corta 'whatsapp' del inbox nativo: comparar
    // contra la corta dejaba la burbuja sin resumen en todo retailer migrado.
    if (message?.contentWsOrder && isWhatsAppMsPlatform(platform)) {
      const { products = [], total = 0 } = message.contentWsOrder;
      setUrl(products[0]?.url || '');
      setMessageText(
        `${products.reduce((previous, current) => previous + (current.quantity || 0), 0)} ${t('orders.items')}\n$ ${formatMoney(total / 1000)} ${t('orders.total_estimated')}`
      );
    }
  }, [platform, message]);

  // Memoización para evitar recomputaciones
  const memoized = useMemo(() => ({
    data: {
      contentMediaUrl: url,
      ...message
    },
    messageText,
    url
  }), [url, messageText]);

  return memoized;
}
